import {
  ENHANCED_MONITORING_PARCEL_UNAVAILABLE_MESSAGE,
  getObjLength,
  propertyHasUsableSiteAddress,
} from "./utils";
import { isLoggedInSuperOrInternalAdmin } from "./eldoradoSignup";

export const PROPERTY_SEARCH_MODE = {
  ADDRESS: "address",
  PARCEL: "parcel",
};

const PROPERTY_SEARCH_MODE_SESSION_KEY = "property_search_mode";

export const isEnhancedMonitoringCategory = (category) =>
  category === "enhanced_monitoring";

export const isParcelSearchPayload = (payload = {}) =>
  payload?.type === "parcel";

/**
 * Block parcel/APN search only when Enhanced Monitoring was chosen *before*
 * search (admin/SP category-first, signup from pricing).
 *
 * Exception: Super Admin / Internal Admin may use Parcel/APN with Enhanced.
 *
 * Customer/SP personal add-property on /properties is search-first: always
 * allow parcel; gate Enhanced Monitoring later via canSelectEnhancedMonitoring.
 */
export const canUseParcelSearch = (
  category,
  { categoryChosenBeforeSearch = false } = {},
) => {
  if (!categoryChosenBeforeSearch) {
    return true;
  }
  if (!isEnhancedMonitoringCategory(category)) {
    return true;
  }
  return isLoggedInSuperOrInternalAdmin();
};

/**
 * Enhanced Monitoring is unavailable when the user searched by APN/parcel
 * or when the property has no usable street site address.
 *
 * Exception: Super Admin / Internal Admin may always select Enhanced
 * (parcel search, missing site address, Add Subscription, etc.).
 */
export const canSelectEnhancedMonitoring = ({
  searchMode = null,
  property = null,
  profile = null,
} = {}) => {
  if (isLoggedInSuperOrInternalAdmin(profile)) {
    return true;
  }
  const mode = searchMode ?? getPropertySearchMode();
  if (mode === PROPERTY_SEARCH_MODE.PARCEL) {
    return false;
  }
  if (getObjLength(property) && !propertyHasUsableSiteAddress(property)) {
    return false;
  }
  return true;
};

export const getEnhancedMonitoringUnavailableMessage = () =>
  ENHANCED_MONITORING_PARCEL_UNAVAILABLE_MESSAGE;

export const setPropertySearchMode = (mode) => {
  if (
    mode !== PROPERTY_SEARCH_MODE.ADDRESS &&
    mode !== PROPERTY_SEARCH_MODE.PARCEL
  ) {
    return;
  }
  try {
    sessionStorage.setItem(PROPERTY_SEARCH_MODE_SESSION_KEY, mode);
  } catch (_error) {
    // Ignore sessionStorage failures (private mode / quota).
  }
};

export const getPropertySearchMode = () => {
  try {
    const mode = sessionStorage.getItem(PROPERTY_SEARCH_MODE_SESSION_KEY);
    if (
      mode === PROPERTY_SEARCH_MODE.ADDRESS ||
      mode === PROPERTY_SEARCH_MODE.PARCEL
    ) {
      return mode;
    }
  } catch (_error) {
    // Ignore sessionStorage failures.
  }
  return null;
};

export const clearPropertySearchMode = () => {
  try {
    sessionStorage.removeItem(PROPERTY_SEARCH_MODE_SESSION_KEY);
  } catch (_error) {
    // Ignore sessionStorage failures.
  }
};

export const setPropertySearchModeFromPayload = (payload = {}) => {
  setPropertySearchMode(
    isParcelSearchPayload(payload)
      ? PROPERTY_SEARCH_MODE.PARCEL
      : PROPERTY_SEARCH_MODE.ADDRESS,
  );
};

// Re-export so callers can import everything from one place.
export {
  ENHANCED_MONITORING_PARCEL_UNAVAILABLE_MESSAGE,
  propertyHasUsableSiteAddress,
};
